import type { ComponentRecord } from "../../types/components";
import { VerifiedPendingBadge } from "./VerifiedPendingBadge";

interface Props {
  component: ComponentRecord;
  selected: boolean;
  onSelect: () => void;
}

/** A single component card in the Components workflow step browser. */
export function ComponentCard({ component, selected, onSelect }: Props) {
  const summary = renderSummary(component);
  return (
    <button
      type="button"
      data-testid={`component-card-${component.id}`}
      onClick={onSelect}
      style={{
        textAlign: "left",
        border: selected ? "2px solid #2563eb" : "1px solid #e5e7eb",
        borderRadius: 8,
        padding: "0.75rem 1rem",
        background: selected ? "#eff6ff" : "white",
        cursor: "pointer",
        width: "100%",
        marginBottom: "0.5rem",
      }}
    >
      <div style={{ fontWeight: 600 }}>{component.manufacturer}</div>
      <div style={{ fontSize: "0.9rem", color: "#374151" }}>
        {component.model_number}
      </div>
      <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.25rem" }}>
        {summary}
      </div>
      <div
        style={{
          fontSize: "0.7rem",
          color: "#9ca3af",
          marginTop: "0.25rem",
        }}
      >
        {component.source} · {component.confidence}
      </div>
      <div style={{ marginTop: "0.4rem" }}>
        <VerifiedPendingBadge component={component} />
      </div>
    </button>
  );
}

function renderSummary(component: ComponentRecord): string {
  const spec = component.spec_data;
  if (component.component_type === "module") {
    const pmp = spec.pmp_w as number | undefined;
    const eff = spec.efficiency_pct as number | undefined;
    return [pmp ? `${pmp}W` : "", eff ? `${eff.toFixed(1)}%` : ""]
      .filter(Boolean)
      .join(" · ");
  }
  if (component.component_type === "inverter") {
    const ac = spec.rated_ac_power_w as number | undefined;
    const type = spec.inverter_type as string | undefined;
    return [ac ? `${(ac / 1000).toFixed(0)} kW` : "", type ?? ""]
      .filter(Boolean)
      .join(" · ");
  }
  // tracker
  const size = spec.table_size_modules as number | undefined;
  const range = spec.tracking_range_deg as number | undefined;
  return [size ? `${size} modules` : "", range ? `±${range}°` : ""]
    .filter(Boolean)
    .join(" · ");
}
